import {
  getFontCss,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import { useQueries, UseQueryResult } from '@tanstack/react-query'
import axios from 'axios'
import React, { useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import UserDropdown, {
  UserDropdownInfiniteScrollProps,
  userIdOption,
  UserOption,
} from 'src/components/UserDropdown/UserDropdown'
import { useDropdownInfiniteScroll } from 'src/hooks/useDropdownInfiniteScroll'
import { translateSingleString } from 'src/localization/Trns'
import { UserListModel, UserListResponse } from 'src/models/user.models'
import { getResponseData } from 'src/queries/network.utils'
import { userQueryKeys } from 'src/queries/queries.constants'
import { getRequestConfig, RequestConfig } from 'src/queries/requestConfig'
import { useQueryUsersSearch } from 'src/queries/users.queries'
import { selectNullSafeLocaleFromState } from 'src/redux/selectors/auth.selectors'
import { selectUserListModelsFromState } from 'src/redux/selectors/data/users.selectors'
import { css } from 'styled-components'

interface VirtualCardUserFilterProps {
  isYourCards?: boolean
  selectedRecipients?: Set<string>
  resetAllFilters: boolean
  setResetAllFilters: (resetAllFilters: boolean) => void
  onToggleRecipient: (recipientId: string) => void
  onResetRecipients: () => void
}

const VirtualCardUserFilter: React.FC<VirtualCardUserFilterProps> = ({
  isYourCards,
  selectedRecipients,
  resetAllFilters,
  setResetAllFilters,
  onToggleRecipient,
  onResetRecipients,
}): React.ReactElement | null => {
  const locale = useSelector(selectNullSafeLocaleFromState)
  const usersById = useSelector(selectUserListModelsFromState)
  const [userSearch, setUserSearch] = useState('')
  const [usersSearchEnabled, setUsersSearchEnabled] = useState(false)

  const {
    data: usersSearchData,
    fetchNextPage: fetchNextUsersPage,
    isFetching: usersFetching,
    isFetchingNextPage: usersFetchingNextPage,
    complete: usersComplete = false,
  } = useQueryUsersSearch({
    filters: {
      q: userSearch,
      count: 10,
    },
    enabled:
      !isYourCards &&
      (usersSearchEnabled || (selectedRecipients?.size ?? 0) > 0),
  })

  const selectedRecipientsMissingFromCurrentData = useMemo(() => {
    if (isYourCards || selectedRecipients === undefined) {
      return []
    }

    const visibleUsersById = new Set(
      usersSearchData?.users?.map((user) => user.id) ?? [],
    )

    return Array.from(selectedRecipients).filter(
      (recipientId) =>
        usersById[recipientId] === undefined &&
        !visibleUsersById.has(recipientId),
    )
  }, [isYourCards, selectedRecipients, usersById, usersSearchData?.users])

  const selectedRecipientUsers = useQueries({
    queries: selectedRecipientsMissingFromCurrentData.map((recipientId) => ({
      queryKey: userQueryKeys.detail(recipientId),
      queryFn: async (): Promise<UserListModel> => {
        const user = await getResponseData(
          axios.get<UserListResponse>(
            `users/${recipientId}`,
            getRequestConfig(RequestConfig.Default),
          ),
        )
        return new UserListModel(user)
      },
      enabled:
        !isYourCards && selectedRecipientsMissingFromCurrentData.length > 0,
    })),
    combine: (results: Array<UseQueryResult<UserListModel, Error>>) =>
      results
        .map((result) => result.data)
        .filter((user): user is UserListModel => user !== undefined),
  })

  const userInfiniteScroll = useDropdownInfiniteScroll({
    currentPage: usersSearchData?.page,
    isLoading: usersFetching,
    isFetchingNextPage: usersFetchingNextPage,
    isComplete: usersComplete,
    fetchNextPage: fetchNextUsersPage,
    currentSearch: userSearch,
    setSearch: setUserSearch,
  })
  const userInfiniteScrollProps: UserDropdownInfiniteScrollProps = {
    ...userInfiniteScroll,
    fetch: (filterInput?: string, requestedPage?: number): void => {
      setUsersSearchEnabled(true)
      userInfiniteScroll.fetch(filterInput, requestedPage)
    },
  }

  const selectedOptions = useMemo<UserOption[] | undefined>(() => {
    if (selectedRecipients === undefined || selectedRecipients.size === 0) {
      return undefined
    }

    const hydratedOptions = Array.from(selectedRecipients)
      .map((recipientId) => {
        return (
          usersById[recipientId] ??
          usersSearchData?.users?.find((user) => user.id === recipientId) ??
          selectedRecipientUsers.find((user) => user.id === recipientId)
        )
      })
      .filter((user) => user !== undefined)
      .map(userIdOption)

    return hydratedOptions.length > 0 ? hydratedOptions : undefined
  }, [
    selectedRecipients,
    selectedRecipientUsers,
    usersById,
    usersSearchData?.users,
  ])

  if (isYourCards) {
    return null
  }

  return (
    <>
      <div
        css={css`
          ${getFontCss(
            14,
            themeConstants.colors.font_primary,
            styleConstants.fontFamilies.medium,
          )};
        `}
      >
        {translateSingleString(locale, 'Card User')}
      </div>
      <UserDropdown
        css={css`
          margin-top: 8px;
        `}
        data-testid="cardUserDropdown"
        isCreatable={false}
        placeholder={translateSingleString(locale, 'All virtual card users')}
        chooseOption={onToggleRecipient}
        isMulti
        selectedValues={new Set<string>(selectedRecipients)}
        optionGroups={[
          {
            options: usersSearchData?.users?.map(userIdOption) ?? [],
          },
        ]}
        selectedOptions={selectedOptions}
        externalReset={{
          resetClicked: resetAllFilters,
          setResetClicked: setResetAllFilters,
        }}
        onResetClick={onResetRecipients}
        infiniteScrollProps={userInfiniteScrollProps}
        minimalDisplay
      />
    </>
  )
}

export default VirtualCardUserFilter
